// src/pages/Mafia/MafiaAdmin.js
//
// IT Admin screen for managing Mafia Aux permissions.
//
// v2: EmpId-based. Admins pick an employee by name from a searchable dropdown;
// the row stores the EmpId in MafiaAuxPermissions.EmpId. Access is decided by
// EmpId only.
//
// Go-live parity (2026-09-29): the MAFIA SolidWorks add-in stamps the
// engineer's WINDOWS USER NAME on the tables it saves. IT records that name
// here ("Windows user name (SolidWorks)") so those tables show the person's
// name and the person cannot confirm / release a table they made themselves.
// The name never grants access.
//
// Access: rendered as a sub-screen of MenuPage only when whoami returns
// isAdmin: true. Every endpoint it calls is independently gated server-side
// with [Authorize(Roles="IT Admin")].

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Collapse,
  Container,
  Input,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import api from "../../api";
import { useAppDialog } from "./AppDialogProvider";
import { MafiaPageHeader } from "./_shared";
import MafiaTheme from "./MafiaTheme";

export default function MafiaAdmin({ onBack }) {
  const dialog = useAppDialog();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [levels, setLevels] = useState([]);
  // Owner 2026-10-19: the USA and India plants have different SOPs, so a
  // person holds a USA level and an India level ("" = same as the USA level).
  // The India column exists once RoundGoLive37 has run; until then the server
  // says so and the USA level counts in both databases, as before.
  const [indiaLevels, setIndiaLevels] = useState({ available: false, message: "" });

  // Add-permission form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employees, setEmployees] = useState([]);
  const [searchingEmployees, setSearchingEmployees] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [newLevel, setNewLevel] = useState(0);
  const [newLevelIndia, setNewLevelIndia] = useState("");
  const [newWindowsName, setNewWindowsName] = useState("");
  const [saving, setSaving] = useState(false);

  // Per-row edit state: { [id]: { level, windowsUserName } }
  const [rowEdits, setRowEdits] = useState({});

  // Go-live parity (2026-09-29): India legacy permission list. India's old
  // MAFIA Aux kept its levels in the India database by Windows user name; the
  // dashboard reads levels only from the list above (by EmpId). This panel is
  // read-only and "Apply level" uses the same add / update actions as the
  // rest of this page.
  const [indiaOpen, setIndiaOpen] = useState(false);
  const [indiaLoading, setIndiaLoading] = useState(false);
  const [indiaLoaded, setIndiaLoaded] = useState(false);
  const [indiaMessage, setIndiaMessage] = useState("");
  const [indiaRows, setIndiaRows] = useState([]);
  const [indiaApplying, setIndiaApplying] = useState("");

  const levelLabel = useCallback(
    (lvl) => {
      const match = levels.find((l) => Number(l.permissionLevel) === Number(lvl));
      return match ? match.description : `Level ${lvl}`;
    },
    [levels]
  );

  const sortedLevels = useMemo(
    () =>
      [...levels].sort(
        (a, b) => Number(a.permissionLevel) - Number(b.permissionLevel)
      ),
    [levels]
  );

  // The level that counts in the India database: the India level, or the USA
  // level while none is set.
  const indiaLevelOf = useCallback(
    (row) =>
      row.accessLevelIndia === null || row.accessLevelIndia === undefined
        ? Number(row.accessLevel)
        : Number(row.accessLevelIndia),
    []
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [permsRes, levelsRes, indiaRes] = await Promise.all([
        api.get("mafia/admin/permissions"),
        api.get("mafia/admin/permission-levels"),
        api.get("mafia/admin/india-levels").catch(() => null),
      ]);
      setPermissions(Array.isArray(permsRes.data) ? permsRes.data : []);
      setLevels(Array.isArray(levelsRes.data) ? levelsRes.data : []);
      setIndiaLevels({
        available: indiaRes?.data?.available === true,
        message:
          typeof indiaRes?.data?.message === "string" ? indiaRes.data.message : "",
      });
    } catch (err) {
      console.error("Failed to load Mafia Aux permissions", err);
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setLoadError(
          "You do not have access to the Mafia Aux permissions admin. This screen is restricted to IT Admin users."
        );
      } else {
        setLoadError("Failed to load Mafia Aux permissions. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const defaultLevelValue = useMemo(() => {
    if (levels.length === 0) return 0;
    const sorted = [...levels].sort(
      (a, b) => Number(a.permissionLevel) - Number(b.permissionLevel)
    );
    return Number(sorted[0].permissionLevel);
  }, [levels]);

  function openAddForm() {
    setSelectedEmployee(null);
    setEmployeeSearch("");
    setEmployees([]);
    setNewLevel(defaultLevelValue);
    setNewLevelIndia("");
    setNewWindowsName("");
    setShowAddForm(true);
  }

  function closeAddForm() {
    setShowAddForm(false);
    setSelectedEmployee(null);
    setEmployeeSearch("");
    setEmployees([]);
    setNewWindowsName("");
  }

  // The server explains what is wrong with a Windows user name (HTTP 422).
  function windowsNameProblem(err) {
    if (err?.response?.status !== 422) return "";
    const text = err?.response?.data;
    return typeof text === "string" && text.trim()
      ? text.trim()
      : "The Windows user name was not accepted.";
  }

  // The names of a text ("first.last; other.name") in one fixed form. A space
  // does not separate names (same rule as the server).
  function windowsNamesOf(value) {
    return String(value ?? "")
      .split(/[;,\r\n]+/)
      .map((x) => {
        const t = x.trim().toLowerCase();
        const slash = t.lastIndexOf("\\");
        return slash >= 0 ? t.slice(slash + 1) : t;
      })
      .filter(Boolean);
  }

  function sameWindowsNames(a, b) {
    return (
      [...new Set(windowsNamesOf(a))].sort().join(";") ===
      [...new Set(windowsNamesOf(b))].sort().join(";")
    );
  }

  async function handleEmployeeSearch() {
    const term = employeeSearch.trim();
    setSearchingEmployees(true);
    try {
      const res = await api.get("mafia/admin/employees", {
        params: { search: term },
      });
      setEmployees(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Employee search failed", err);
      await dialog.error("Employee search failed. Please try again.");
    } finally {
      setSearchingEmployees(false);
    }
  }

  async function handleCreate() {
    if (!selectedEmployee) {
      await dialog.warning("Select an employee first.");
      return;
    }
    const empId = (selectedEmployee.empId || "").trim();
    if (!empId) {
      await dialog.warning("Selected employee has no EmpId on file.");
      return;
    }

    setSaving(true);
    try {
      const indiaValue = newLevelIndia === "" ? null : Number(newLevelIndia);
      await api.post("mafia/admin/permissions", {
        empId,
        accessLevel: Number(newLevel),
        ...(indiaLevels.available
          ? { accessLevelIndiaSet: true, accessLevelIndia: indiaValue }
          : {}),
        windowsUserName: newWindowsName.trim(),
      });
      await dialog.info(
        indiaLevels.available && indiaValue !== null
          ? `Granted ${selectedEmployee.name} (${empId}) "${levelLabel(
              newLevel
            )}" in the USA database and "${levelLabel(
              indiaValue
            )}" in the India database.`
          : `Granted "${levelLabel(newLevel)}" access to ${selectedEmployee.name} (${empId}).`
      );
      closeAddForm();
      await loadAll();
    } catch (err) {
      console.error("Create permission failed", err);
      const status = err?.response?.status;
      if (status === 422) {
        await dialog.warning(windowsNameProblem(err));
      } else if (status === 409) {
        await dialog.warning(
          `${selectedEmployee.name} (${empId}) already has a Mafia Aux permission row. Edit the existing row instead.`
        );
      } else if (status === 401 || status === 403) {
        await dialog.error("Only IT Admin users can grant Mafia Aux access.");
      } else {
        await dialog.error("Failed to grant access. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  }

  function startRowEdit(row) {
    setRowEdits((prev) => ({
      ...prev,
      [row.id]: {
        level: Number(row.accessLevel),
        levelIndia:
          row.accessLevelIndia === null || row.accessLevelIndia === undefined
            ? ""
            : String(row.accessLevelIndia),
        windowsUserName: row.windowsUserName || "",
      },
    }));
  }

  function cancelRowEdit(id) {
    setRowEdits((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  function setRowEditValue(id, value) {
    setRowEdits((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), level: Number(value) },
    }));
  }

  function setRowEditIndia(id, value) {
    setRowEdits((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), levelIndia: value },
    }));
  }

  function setRowEditWindowsName(id, value) {
    setRowEdits((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), windowsUserName: value },
    }));
  }

  async function handleUpdate(row) {
    const edit = rowEdits[row.id];
    if (edit === undefined) return;
    const newValue = Number(edit.level);
    const levelChanged = newValue !== Number(row.accessLevel);
    const newIndia =
      edit.levelIndia === "" || edit.levelIndia === undefined
        ? null
        : Number(edit.levelIndia);
    const oldIndia =
      row.accessLevelIndia === null || row.accessLevelIndia === undefined
        ? null
        : Number(row.accessLevelIndia);
    const indiaChanged = indiaLevels.available && newIndia !== oldIndia;
    const nameChanged = !sameWindowsNames(
      edit.windowsUserName,
      row.windowsUserName
    );
    if (!levelChanged && !indiaChanged && !nameChanged) {
      cancelRowEdit(row.id);
      return;
    }
    try {
      const body = { empId: row.empId, accessLevel: newValue };
      // Sent only when it changed: leaving it out keeps what is recorded.
      if (indiaChanged) {
        body.accessLevelIndiaSet = true;
        body.accessLevelIndia = newIndia;
      }
      if (nameChanged) {
        body.windowsUserName = String(edit.windowsUserName ?? "").trim();
      }
      await api.put(`mafia/admin/permissions/${row.id}`, body);
      const who = row.employeeName || row.empId;
      await dialog.info(
        levelChanged && indiaChanged
          ? `Updated ${who}: "${levelLabel(newValue)}" in the USA database, ${
              newIndia === null ? "the same" : `"${levelLabel(newIndia)}"`
            } in the India database.`
          : levelChanged
          ? `Updated the USA level of ${who} to "${levelLabel(newValue)}".`
          : indiaChanged
          ? newIndia === null
            ? `${who} now has the same level in the India database as in the USA.`
            : `Updated the India level of ${who} to "${levelLabel(newIndia)}".`
          : `Updated the Windows user name of ${who}.`
      );
      cancelRowEdit(row.id);
      await loadAll();
    } catch (err) {
      console.error("Update permission failed", err);
      const status = err?.response?.status;
      if (status === 422) {
        await dialog.warning(windowsNameProblem(err));
      } else if (status === 404) {
        await dialog.warning("That permission row no longer exists. Refreshing.");
        cancelRowEdit(row.id);
        await loadAll();
      } else if (status === 401 || status === 403) {
        await dialog.error("Only IT Admin users can change Mafia Aux access.");
      } else {
        await dialog.error("Failed to update access. Please try again.");
      }
    }
  }

  async function handleDelete(row) {
    const who = row.employeeName ? `${row.employeeName} (${row.empId})` : row.empId;
    const confirmed = await dialog.confirm(
      `Remove Mafia Aux access for ${who}? They will become non-credentialed.`
    );
    if (!confirmed) return;

    try {
      await api.delete(`mafia/admin/permissions/${row.id}`);
      await dialog.info(`Removed Mafia Aux access for ${who}.`);
      await loadAll();
    } catch (err) {
      console.error("Delete permission failed", err);
      const status = err?.response?.status;
      if (status === 404) {
        await dialog.warning("That permission row no longer exists. Refreshing.");
        await loadAll();
      } else if (status === 401 || status === 403) {
        await dialog.error("Only IT Admin users can remove Mafia Aux access.");
      } else {
        await dialog.error("Failed to remove access. Please try again.");
      }
    }
  }

  function formatDate(value) {
    if (!value) return "—";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
  }

  // ── Go-live parity (2026-09-29): India legacy permission list ────────────
  const loadIndiaLegacy = useCallback(async () => {
    setIndiaLoading(true);
    setIndiaMessage("");
    try {
      const res = await api.get("mafia/admin/india-legacy-permissions");
      const data = res && res.data ? res.data : {};
      setIndiaRows(Array.isArray(data.rows) ? data.rows : []);
      setIndiaMessage(
        data.available === false
          ? data.message || "The India legacy permission list is not available."
          : ""
      );
    } catch (err) {
      console.error("Failed to load the India legacy permission list", err);
      const status = err?.response?.status;
      setIndiaRows([]);
      setIndiaMessage(
        status === 401 || status === 403
          ? "Only IT Admin users can see the India legacy permission list."
          : "Failed to load the India legacy permission list. Please try again."
      );
    } finally {
      setIndiaLoaded(true);
      setIndiaLoading(false);
    }
  }, []);

  function toggleIndiaPanel() {
    const next = !indiaOpen;
    setIndiaOpen(next);
    if (next && !indiaLoaded && !indiaLoading) loadIndiaLegacy();
  }

  // The row of the list above (the one list the dashboard reads) per EmpId.
  const permissionByEmpId = useMemo(() => {
    const map = new Map();
    permissions.forEach((p) => {
      const key = String(p.empId ?? "").trim().toLowerCase();
      if (key && !map.has(key)) map.set(key, p);
    });
    return map;
  }, [permissions]);

  function currentPermissionOf(account) {
    const key = String(account?.number ?? "").trim().toLowerCase();
    return key ? permissionByEmpId.get(key) || null : null;
  }

  // Reloads the list above without the full-page spinner, so the long India
  // table stays where the user was working.
  async function refreshPermissions() {
    try {
      const res = await api.get("mafia/admin/permissions");
      setPermissions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to refresh Mafia Aux permissions", err);
      await loadAll();
    }
  }

  // True when the Windows user name of an India legacy row is already known
  // for the account: recorded in the list above, or the account's Windows ID.
  function legacyNameIsRecorded(row, existing) {
    const legacy = windowsNamesOf(row.windowsUserName)[0] || "";
    if (!legacy || !existing) return false;
    return (
      windowsNamesOf(existing.windowsUserName).includes(legacy) ||
      windowsNamesOf(existing.accountWindowsId).includes(legacy)
    );
  }

  async function handleApplyIndiaLevel(row) {
    const account = row.proposedAccount;
    const empId = String(account?.number ?? "").trim();
    const level = Number(row.legacyLevel);
    if (!empId || !Number.isFinite(level)) return;

    const existing = currentPermissionOf(account);
    // Owner 2026-10-19: a legacy India level goes into the INDIA level of the
    // account. The USA level is never touched by this panel.
    if (!indiaLevels.available) {
      await dialog.warning(
        indiaLevels.message || "The India level is not available yet."
      );
      return;
    }
    const who = `${account.name || empId} (${empId}, ${
      account.isIndia ? "India" : "USA"
    } account)`;
    const legacyName = windowsNamesOf(row.windowsUserName)[0] || "";
    const currentIndia = existing ? indiaLevelOf(existing) : null;
    const sameLevel = !!existing && Number(currentIndia) === level;
    const recordName = !!legacyName && !legacyNameIsRecorded(row, existing);
    if (sameLevel && !recordName) return;

    const floridaLine =
      existing && !account.isIndia && !sameLevel
        ? `\n\nThis is a Florida (USA) employee. Only the India level changes; the USA level stays "${levelLabel(
            existing.accessLevel
          )}".`
        : "";
    const nameLine =
      floridaLine +
      (recordName
        ? `\n\nThe Windows user name "${legacyName}" is recorded for this person, so the tables made under that name in SolidWorks are known as theirs.`
        : "");
    const confirmed = await dialog.confirm(
      sameLevel
        ? `Record the Windows user name "${legacyName}" for ${who}? The tables made under that name in SolidWorks are then known as theirs. The India level stays "${levelLabel(
            level
          )}".`
        : existing
        ? `Change the INDIA level of ${who} from "${levelLabel(
            currentIndia
          )}" to "${levelLabel(level)}" (India legacy level ${level})?${nameLine}`
        : `Grant the INDIA level "${levelLabel(
            level
          )}" (India legacy level ${level}) to ${who}? In the USA database the person stays non-credentialed (level 0).${nameLine}`
    );
    if (!confirmed) return;

    setIndiaApplying(row.windowsUserName);
    try {
      // Same actions as "Grant Access" / "Edit" above — no separate write.
      // The names already recorded for the person are kept.
      const body = {
        empId,
        accessLevel: existing ? Number(existing.accessLevel) : 0,
        accessLevelIndiaSet: true,
        accessLevelIndia: level,
      };
      if (recordName) {
        body.windowsUserName = [
          ...new Set([
            ...windowsNamesOf(existing?.windowsUserName),
            legacyName,
          ]),
        ].join("; ");
      }
      if (existing) {
        await api.put(`mafia/admin/permissions/${existing.id}`, body);
      } else {
        await api.post("mafia/admin/permissions", body);
      }
      await dialog.info(
        sameLevel
          ? `Recorded the Windows user name "${legacyName}" for ${who}.`
          : `Set the India level of ${who} to "${levelLabel(level)}".`
      );
    } catch (err) {
      console.error("Apply India legacy level failed", err);
      const status = err?.response?.status;
      if (status === 422) {
        await dialog.warning(windowsNameProblem(err));
      } else if (status === 409) {
        await dialog.warning(
          `${who} already has a Mafia Aux permission row. The list will be refreshed now — use "Apply level" again to change it.`
        );
      } else if (status === 404) {
        await dialog.warning("That permission row no longer exists. Refreshing.");
      } else if (status === 401 || status === 403) {
        await dialog.error("Only IT Admin users can change Mafia Aux access.");
      } else {
        await dialog.error("Failed to apply the level. Please try again.");
      }
    } finally {
      // "Current level" of every India row is read from the list above.
      await refreshPermissions();
      setIndiaApplying("");
    }
  }

  return (
    <Container fluid className="p-3 mafia-screen">
      <MafiaTheme />
      <MafiaPageHeader
        icon="ri-shield-user-line"
        eyebrow="MAFIA Aux"
        title="Permissions Admin"
        subtitle="Grant, change, or remove Mafia Aux access by employee. IT Admin only."
        tone="violet"
        actions={
          <>
            {typeof onBack === "function" && (
              <Button
                color="light"
                onClick={onBack}
                style={{
                  background: "rgba(255,255,255,0.92)",
                  border: "none",
                  fontWeight: 500,
                }}
              >
                <i className="ri-arrow-left-line align-bottom me-1" />
                Back to Menu
              </Button>
            )}
            <Button
              color="light"
              onClick={loadAll}
              disabled={loading}
              style={{
                background: "rgba(255,255,255,0.18)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.3)",
                fontWeight: 500,
              }}
            >
              <i className="ri-refresh-line align-bottom me-1" />
              Refresh
            </Button>
          </>
        }
        breadcrumb={[
          { label: "Applications", to: "/" },
          { label: "MAFIA Aux", onClick: onBack },
          { label: "Permissions Admin" },
        ]}
      />

      {loadError ? (
        <Alert color="danger">{loadError}</Alert>
      ) : loading ? (
        <div className="text-center p-5">
          <Spinner /> <span className="ms-2">Loading permissions…</span>
        </div>
      ) : (
        <>
          {!indiaLevels.available && indiaLevels.message ? (
            <Alert color="warning">{indiaLevels.message}</Alert>
          ) : null}

          {/* Add permission */}
          <Card className="mb-3">
            <CardBody>
              {!showAddForm ? (
                <Button color="primary" onClick={openAddForm}>
                  <i className="ri-add-line align-bottom" /> Grant Access
                </Button>
              ) : (
                <div>
                  <h6 className="mb-3">Grant Mafia Aux access</h6>
                  <Row className="g-2 align-items-end">
                    <Col md={5}>
                      <label className="form-label mb-1">
                        Find employee (name or EmpId)
                      </label>
                      <div className="d-flex gap-2">
                        <Input
                          value={employeeSearch}
                          placeholder="e.g. Sharma or 3325"
                          onChange={(e) => setEmployeeSearch(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleEmployeeSearch();
                          }}
                        />
                        <Button
                          color="secondary"
                          onClick={handleEmployeeSearch}
                          disabled={searchingEmployees}
                        >
                          {searchingEmployees ? <Spinner size="sm" /> : "Search"}
                        </Button>
                      </div>
                    </Col>
                    <Col md={4}>
                      <label className="form-label mb-1">Employee</label>
                      <Input
                        type="select"
                        value={selectedEmployee ? selectedEmployee.empId : ""}
                        onChange={(e) => {
                          const emp = employees.find(
                            (x) => x.empId === e.target.value
                          );
                          setSelectedEmployee(emp || null);
                        }}
                      >
                        <option value="">
                          {employees.length === 0
                            ? "— search first —"
                            : "— select employee —"}
                        </option>
                        {employees.map((emp) => (
                          <option key={emp.empId} value={emp.empId}>
                            {emp.name} ({emp.empId})
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={3}>
                      <label className="form-label mb-1">USA level</label>
                      <Input
                        type="select"
                        value={newLevel}
                        onChange={(e) => setNewLevel(Number(e.target.value))}
                      >
                        {levels
                          .slice()
                          .sort(
                            (a, b) =>
                              Number(a.permissionLevel) -
                              Number(b.permissionLevel)
                          )
                          .map((l) => (
                            <option
                              key={l.permissionLevel}
                              value={l.permissionLevel}
                            >
                              {l.permissionLevel} — {l.description}
                            </option>
                          ))}
                      </Input>
                    </Col>
                  </Row>
                  <Row className="g-2 mt-1">
                    {indiaLevels.available ? (
                      <Col md={3}>
                        <label className="form-label mb-1">India level</label>
                        <Input
                          type="select"
                          value={newLevelIndia}
                          onChange={(e) => setNewLevelIndia(e.target.value)}
                        >
                          <option value="">Same as USA</option>
                          {sortedLevels.map((l) => (
                            <option key={l.permissionLevel} value={l.permissionLevel}>
                              {l.permissionLevel} — {l.description}
                            </option>
                          ))}
                        </Input>
                      </Col>
                    ) : null}
                    <Col md={5}>
                      <label className="form-label mb-1">
                        Windows user name (SolidWorks){" "}
                        <span className="text-muted">— optional</span>
                      </label>
                      <Input
                        value={newWindowsName}
                        placeholder="e.g. first.last"
                        maxLength={255}
                        onChange={(e) => setNewWindowsName(e.target.value)}
                      />
                    </Col>
                    <Col
                      md={indiaLevels.available ? 4 : 7}
                      className="d-flex align-items-end"
                    >
                      <small className="text-muted">
                        The name the person signs in to Windows with. The MAFIA
                        add-in in SolidWorks stamps it on the tables the person
                        saves. It does not give any access.
                      </small>
                    </Col>
                  </Row>
                  <div className="mt-3 d-flex gap-2">
                    <Button
                      color="primary"
                      onClick={handleCreate}
                      disabled={saving || !selectedEmployee}
                    >
                      {saving ? <Spinner size="sm" /> : "Save"}
                    </Button>
                    <Button color="light" onClick={closeAddForm} disabled={saving}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Permissions table */}
          <Card>
            <CardBody>
              <h6 className="mb-1">
                Current permissions{" "}
                <Badge color="light" className="text-dark">
                  {permissions.length}
                </Badge>
              </h6>
              <small className="text-muted d-block mb-3">
                The USA level counts in the USA MAFIA database, the India level
                in the India MAFIA database (the plants have different SOPs).
                &quot;Same as USA&quot; means one level everywhere.
              </small>
              {permissions.length === 0 ? (
                <p className="text-muted mb-0">
                  No Mafia Aux permissions have been granted yet.
                </p>
              ) : (
                <Table responsive hover className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>EmpId</th>
                      <th>Windows user name (SolidWorks)</th>
                      <th>USA level</th>
                      {indiaLevels.available ? <th>India level</th> : null}
                      <th>Created</th>
                      <th>Updated</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {permissions.map((row) => {
                      const editing = rowEdits[row.id] !== undefined;
                      return (
                        <tr key={row.id}>
                          <td>{row.employeeName || <em className="text-muted">unknown</em>}</td>
                          <td><code>{row.empId}</code></td>
                          <td>
                            {editing ? (
                              <Input
                                bsSize="sm"
                                style={{ maxWidth: 240 }}
                                value={rowEdits[row.id].windowsUserName ?? ""}
                                placeholder="e.g. first.last"
                                maxLength={255}
                                onChange={(e) =>
                                  setRowEditWindowsName(row.id, e.target.value)
                                }
                              />
                            ) : row.windowsUserName ? (
                              <code>{row.windowsUserName}</code>
                            ) : row.accountWindowsId ? (
                              <>
                                <code>{row.accountWindowsId}</code>{" "}
                                <small className="text-muted">
                                  (from the old system)
                                </small>
                              </>
                            ) : (
                              <Badge
                                color="warning"
                                title="Tables this person saves from SolidWorks are not known as theirs until the Windows user name is recorded."
                              >
                                not recorded
                              </Badge>
                            )}
                          </td>
                          <td>
                            {editing ? (
                              <Input
                                type="select"
                                bsSize="sm"
                                style={{ maxWidth: 220 }}
                                value={rowEdits[row.id].level}
                                onChange={(e) =>
                                  setRowEditValue(row.id, e.target.value)
                                }
                              >
                                {levels
                                  .slice()
                                  .sort(
                                    (a, b) =>
                                      Number(a.permissionLevel) -
                                      Number(b.permissionLevel)
                                  )
                                  .map((l) => (
                                    <option
                                      key={l.permissionLevel}
                                      value={l.permissionLevel}
                                    >
                                      {l.permissionLevel} — {l.description}
                                    </option>
                                  ))}
                              </Input>
                            ) : (
                              <>
                                <Badge color="info" className="me-2">
                                  {row.accessLevel}
                                </Badge>
                                {levelLabel(row.accessLevel)}
                              </>
                            )}
                          </td>
                          {indiaLevels.available ? (
                            <td>
                              {editing ? (
                                <Input
                                  type="select"
                                  bsSize="sm"
                                  style={{ maxWidth: 220 }}
                                  value={rowEdits[row.id].levelIndia ?? ""}
                                  onChange={(e) =>
                                    setRowEditIndia(row.id, e.target.value)
                                  }
                                >
                                  <option value="">Same as USA</option>
                                  {sortedLevels.map((l) => (
                                    <option
                                      key={l.permissionLevel}
                                      value={l.permissionLevel}
                                    >
                                      {l.permissionLevel} — {l.description}
                                    </option>
                                  ))}
                                </Input>
                              ) : row.accessLevelIndia === null ||
                                row.accessLevelIndia === undefined ? (
                                <small className="text-muted">
                                  same as USA ({row.accessLevel})
                                </small>
                              ) : (
                                <>
                                  <Badge color="warning" className="me-2">
                                    {row.accessLevelIndia}
                                  </Badge>
                                  {levelLabel(row.accessLevelIndia)}
                                </>
                              )}
                            </td>
                          ) : null}
                          <td>
                            <small>
                              {formatDate(row.dateCreated)}
                              {row.createdBy ? (
                                <span className="text-muted">
                                  {" "}
                                  by {row.createdBy}
                                </span>
                              ) : null}
                            </small>
                          </td>
                          <td>
                            <small>
                              {formatDate(row.dateUpdated)}
                              {row.updatedBy ? (
                                <span className="text-muted">
                                  {" "}
                                  by {row.updatedBy}
                                </span>
                              ) : null}
                            </small>
                          </td>
                          <td className="text-end">
                            {editing ? (
                              <>
                                <Button
                                  color="primary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => handleUpdate(row)}
                                >
                                  Save
                                </Button>
                                <Button
                                  color="light"
                                  size="sm"
                                  onClick={() => cancelRowEdit(row.id)}
                                >
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  color="secondary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => startRowEdit(row)}
                                >
                                  <i className="ri-pencil-line align-bottom" />{" "}
                                  Edit
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleDelete(row)}
                                >
                                  <i className="ri-delete-bin-line align-bottom" />{" "}
                                  Remove
                                </Button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>

          {/* Go-live parity (2026-09-29): India legacy permission list —
              read-only; "Apply level" uses the add / update actions above. */}
          <Card className="mt-3">
            <CardBody>
              <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
                <div>
                  <h6 className="mb-1">
                    India legacy permission list{" "}
                    {indiaLoaded && !indiaMessage ? (
                      <Badge color="light" className="text-dark">
                        {indiaRows.length}
                      </Badge>
                    ) : null}
                  </h6>
                  <small className="text-muted">
                    The levels India&apos;s old MAFIA Aux used, kept in the India
                    database by Windows user name. The dashboard reads levels
                    only from the list above — use &quot;Apply level&quot; to
                    carry a level over as the INDIA level of the matching
                    account (the USA level is not changed). The Windows user
                    name is recorded for the account at the same time.
                  </small>
                </div>
                <div className="d-flex gap-2">
                  {indiaOpen ? (
                    <Button
                      color="light"
                      size="sm"
                      onClick={loadIndiaLegacy}
                      disabled={indiaLoading}
                    >
                      <i className="ri-refresh-line align-bottom" /> Refresh
                    </Button>
                  ) : null}
                  <Button color="secondary" size="sm" onClick={toggleIndiaPanel}>
                    <i
                      className={`ri-arrow-${
                        indiaOpen ? "up" : "down"
                      }-s-line align-bottom`}
                    />{" "}
                    {indiaOpen ? "Hide" : "Show"}
                  </Button>
                </div>
              </div>

              <Collapse isOpen={indiaOpen}>
                <div className="mt-3">
                  {indiaLoading ? (
                    <div className="text-center p-3">
                      <Spinner size="sm" />{" "}
                      <span className="ms-2">
                        Loading the India legacy permission list…
                      </span>
                    </div>
                  ) : indiaMessage ? (
                    <Alert color="warning" className="mb-0">
                      {indiaMessage}
                    </Alert>
                  ) : indiaRows.length === 0 ? (
                    <p className="text-muted mb-0">
                      The India legacy permission list is empty.
                    </p>
                  ) : (
                    <Table responsive hover className="align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Windows user name</th>
                          <th>Legacy level</th>
                          <th>Proposed account</th>
                          <th>Matched by</th>
                          <th>Current India level</th>
                          <th>Last updated</th>
                          <th className="text-end">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {indiaRows.map((row, idx) => {
                          const account = row.proposedAccount || null;
                          const existing = account
                            ? currentPermissionOf(account)
                            : null;
                          const sameLevel =
                            existing &&
                            Number(indiaLevelOf(existing)) ===
                              Number(row.legacyLevel);
                          const nameRecorded = legacyNameIsRecorded(
                            row,
                            existing
                          );
                          const applying =
                            indiaApplying !== "" &&
                            indiaApplying === row.windowsUserName;
                          return (
                            <tr key={`${row.windowsUserName}-${idx}`}>
                              <td>
                                <code>{row.windowsUserName}</code>
                              </td>
                              <td>
                                <Badge color="info" className="me-2">
                                  {row.legacyLevel}
                                </Badge>
                                {levelLabel(row.legacyLevel)}
                              </td>
                              <td>
                                {account ? (
                                  <>
                                    {account.name || (
                                      <em className="text-muted">unknown</em>
                                    )}{" "}
                                    <code>{account.number}</code>{" "}
                                    <Badge
                                      color={account.isIndia ? "warning" : "light"}
                                      className={account.isIndia ? "" : "text-dark"}
                                    >
                                      {account.isIndia ? "India" : "USA"}
                                    </Badge>
                                    {account.active ? null : (
                                      <Badge color="secondary" className="ms-1">
                                        inactive
                                      </Badge>
                                    )}
                                  </>
                                ) : row.note ? (
                                  // More than one account fits: nothing is
                                  // proposed, IT picks the account itself.
                                  <span className="text-muted">{row.note}</span>
                                ) : (
                                  <span className="text-muted">
                                    no account yet — create it in HR &gt; India
                                    Employees
                                  </span>
                                )}
                              </td>
                              <td>
                                {row.matchKind === "windowsId"
                                  ? "Windows ID"
                                  : row.matchKind === "name"
                                  ? "Name"
                                  : "—"}
                              </td>
                              <td>
                                {!account ? (
                                  "—"
                                ) : existing ? (
                                  <>
                                    <Badge color="info" className="me-2">
                                      {indiaLevelOf(existing)}
                                    </Badge>
                                    {levelLabel(indiaLevelOf(existing))}
                                  </>
                                ) : (
                                  <span className="text-muted">none</span>
                                )}
                              </td>
                              <td>
                                <small>{formatDate(row.dateUpdated)}</small>
                              </td>
                              <td className="text-end">
                                {!account ? null : !account.active ? (
                                  <small className="text-muted">
                                    account is inactive
                                  </small>
                                ) : sameLevel && nameRecorded ? (
                                  <small className="text-muted">same level</small>
                                ) : sameLevel ? (
                                  // The level is right; only the Windows user
                                  // name is not recorded for the account yet.
                                  <Button
                                    color="secondary"
                                    size="sm"
                                    onClick={() => handleApplyIndiaLevel(row)}
                                    disabled={indiaApplying !== ""}
                                  >
                                    {applying ? (
                                      <Spinner size="sm" />
                                    ) : (
                                      "Record user name"
                                    )}
                                  </Button>
                                ) : (
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => handleApplyIndiaLevel(row)}
                                    disabled={indiaApplying !== ""}
                                  >
                                    {applying ? <Spinner size="sm" /> : "Apply level"}
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  )}
                </div>
              </Collapse>
            </CardBody>
          </Card>
        </>
      )}
    </Container>
  );
}
